import axios from 'axios'

import {
  ListDistrictsQuery,
  ListRegionsQuery
} from 'src/config/generated/graphql'
import { ArrayElement } from 'src/types/common'
import { GeoData } from 'src/types/map'
import { logInfo } from 'src/utils/errors'

class MS {
  url = process.env.NEXT_PUBLIC_GOOGLE_MAPS_ENDPOINT

  format = 'json'

  key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY

  getGeoCodeByLatLng = async (geo: google.maps.LatLngLiteral) => {
    const res = await axios.get(
      `${this.url}/geocode/${this.format}?key=${this.key}&latlng=${geo.lat},${geo.lng}`
    )
    return res.data
  }
}

const MapServices = new MS()

export const isValidGeo = (geo?: GeoData) => {
  return geo?.lat && geo?.lng
}

function getRegionDistrictFromAddressResults(
  addressResults: google.maps.places.PlaceResult[]
) {
  const componentRegionForm: { [key: string]: string[] } = {
    administrative_area_level_1: ['long_name', 'region'],
    administrative_area_level_2: ['long_name', 'district'],
    street_number: ['short_name', 'street_number'],
    route: ['long_name', 'street_name'],
    locality: ['long_name', 'city'],
    country: ['long_name', 'country'],
    postal_code: ['long_name', 'postal_code']
  }

  const regionDistrict: { [key: string]: string } = {}

  addressResults.forEach((place: google.maps.places.PlaceResult) => {
    place.address_components?.forEach(
      (ac: google.maps.GeocoderAddressComponent) => {
        const addressType = ac.types[0]!

        if (addressType in componentRegionForm) {
          if (regionDistrict[addressType]) return
          const val = (ac as any)[componentRegionForm[addressType]![0]!]
          regionDistrict[addressType] = val
        }
      }
    )
  })

  return {
    region: regionDistrict.administrative_area_level_1,
    district: regionDistrict.administrative_area_level_2,
    street_number: regionDistrict.street_number,
    street_name: regionDistrict.route,
    city: regionDistrict.locality,
    country: regionDistrict.country,
    postal_code: regionDistrict.postal_code
  }
}

export function getRegionDistrictFromPlace(
  addressComponents: google.maps.GeocoderAddressComponent[],
  componentRegionForm: { [key: string]: string[] }
) {
  const regionDistrict: { [key: string]: string } = {}

  addressComponents.forEach((ac: google.maps.GeocoderAddressComponent) => {
    const addressType = ac.types[0]!

    if (addressType in componentRegionForm) {
      const val = (ac as any)[componentRegionForm[addressType]![0]!]
      regionDistrict[addressType] = val
    }
  })

  return {
    region: regionDistrict.administrative_area_level_1,
    district: regionDistrict.administrative_area_level_2
  }
}

function getLocationDetail(addressResult: google.maps.places.PlaceResult[]) {
  return addressResult.length === 0
    ? 'Cannot determine address at this location.'
    : addressResult[0]!.formatted_address
}

// get place_id, district, region data
function getPlaceData(addressResults: google.maps.places.PlaceResult[]) {
  const regionDistrict = getRegionDistrictFromAddressResults(addressResults)
  return {
    place_id: addressResults[0]!.place_id,
    ...regionDistrict
  }
}

export async function geoCodePositionFromMarker(
  geoLocation: google.maps.LatLngLiteral
) {
  return MapServices.getGeoCodeByLatLng(geoLocation).then(
    ({ results }: { results: google.maps.places.PlaceResult[] }) => {
      const address = getLocationDetail(results)

      return {
        address,
        geoLocation: { ...geoLocation, ...getPlaceData(results) }
      }
    }
  )
}

export function matchRegionDistrictByName(
  geoData?: GeoData,
  regions?: ArrayElement<ListRegionsQuery['Region']>[],
  districts?: ArrayElement<ListDistrictsQuery['District']>[]
) {
  const region = geoData?.region
    ? regions?.find((r: ArrayElement<ListRegionsQuery['Region']>) =>
        r.Lookups?.includes(geoData.region as string)
      )
    : null

  const district = geoData?.district
    ? districts?.find(
        (d: ArrayElement<ListDistrictsQuery['District']>) =>
          d.Lookups?.includes(geoData.district as string) ||
          (geoData.district as string).includes(d.Name)
      )
    : null

  if (!region) {
    logInfo('Region match failed', {
      geoData,
      matchedRegion: region
    })
  }

  return { region, district }
}
